import { ExcelButton } from "@/controls/buttons/Excel-button";
import { ErrorModal, getMomentTz } from "@dexteel/mesf-core";
import {
	Divider,
	Grid,
	Paper,
	Typography,
	makeStyles,
} from "@material-ui/core";
import { ProcessCellForExportParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { get } from "lodash-es";
import { useEffect, useRef, useState } from "react";
import {
	LazyBackDropLoading,
	LazyLoading,
} from "../../../controls/LazyLoading";
import { NotificationSnackBar } from "../../../controls/snackbars/notification-snackbar";
import { DelaysReportTable } from "./components/DelaysReportTable";
import { Filters } from "./components/filters/Filters";
import { useDelaysReportContext } from "./context/DelaysReportContext";
import {
	getDelayAreas,
	getDelays,
} from "./repositories/DelaysReportRepository";

const useStyles = makeStyles((theme) => ({
	root: {
		width: "100%",
		padding: "15px 20px 10px",
		borderRadius: "0.75rem",
		height: "92vh",
		backgroundColor: "#f2f4f7",
	},
	header: {
		marginBottom: theme.spacing(2),
	},
	filterContainer: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: theme.spacing(2),
	},
	button: {
		whiteSpace: "nowrap",
	},
}));

const moment = getMomentTz();
type Props = {
	asset: "caster" | "eaf";
};
export const DelaysReportPage = ({ asset }: Props) => {
	const classes = useStyles();
	const { state, actions } = useDelaysReportContext();

	const [error, setError] = useState("");
	const [filterStartDate, setFilterStartDate] = useState<Date>(() => {
		const startDate = moment()
			.subtract(1, "day")
			.set({ hour: 0, minute: 0, second: 0 })
			.toDate();
		return new Date(startDate);
	});
	const [filterEndDate, setFilterEndDate] = useState<Date>(() => {
		const endDate = moment().set({ hour: 23, minute: 59, second: 59 }).toDate();
		return new Date(endDate);
	});
	const [filterCrew, setFilterCrew] = useState<string | null>("All");
	const [filterShift, setFilterShift] = useState<string | null>("All");

	const assetId = asset === "caster" ? 13 : asset === "eaf" ? 14 : null;
	const assetName =
		asset === "caster" ? "Caster" : asset === "eaf" ? "EAF" : null;
	const [selectedDelayArea, setSelectedDelayArea] = useState<number | null>(
		assetId,
	);
	const [delayAreas, setDelayAreas] = useState<
		Array<{ AssetId: number; AssetName: string }>
	>([]);
	const [includeMicrodelays, setIncludeMicrodelays] = useState<boolean>(false);
	const [initialized, setInitialized] = useState(false);

	const gridRef = useRef<AgGridReact>(null);

	const fetchDelayAreas = async () => {
		const resp = await getDelayAreas();
		if (resp.ok && resp.data.tables.length > 0) {
			const areas = resp.data.tables[0].rows;
			setDelayAreas(areas);
		} else {
			setError("An error occurred while fetching delay areas.");
		}
	};

	const getDataFromAPI = async () => {
		actions.setIsLoadingGrid(true);
		const resp = await getDelays(
			filterStartDate,
			filterEndDate,
			selectedDelayArea as number,
			filterShift as string,
			filterCrew as string,
			includeMicrodelays,
		);

		if (resp.ok) {
			const data = get(resp, "data.tables[0].rows", []);
			actions.setDelays(data);
		} else setError(resp.message);
		actions.setIsLoadingGrid(false);
	};

	const handleFilterChange = (startDate: Date, endDate: Date) => {
		const start = moment(startDate)
			.set({ hour: 0, minute: 0, second: 0 })
			.toDate();
		const end = moment(endDate)
			.set({ hour: 23, minute: 59, second: 59 })
			.toDate();
		setFilterStartDate(start);
		setFilterEndDate(end);
	};

	const handleDelayAreaChange = (assetId: number | null) => {
		setSelectedDelayArea(assetId);
	};

	const handleIncludeMicrodelaysChange = (include: boolean) => {
		setIncludeMicrodelays(include);
	};

	const handleExportToExcel = () => {
		if (gridRef.current && gridRef.current.api) {
			const params = {
				skipHeader: false,
				skipFooters: true,
				skipGroups: true,
				fileName: "delays_report.xlsx",
				sheetName: "Delays Report",
				processCellCallback: (params: ProcessCellForExportParams) => {
					const colId = params.column.getColId();
					const val = params.value;
					//to show the category name
					if (colId === "DelayCategory") {
						return val?.name ?? "";
					}
					return val ?? "";
				},
			};
			gridRef.current.api.exportDataAsExcel(params);
		} else {
			setError("Unable to export data. Please try again.");
		}
	};

	useEffect(() => {
		if (asset === "eaf") {
			fetchDelayAreas();
		}
	}, []);

	useEffect(() => {
		setInitialized(true);
	}, [delayAreas]);

	useEffect(() => {
		setSelectedDelayArea(assetId);
	}, [assetId]);

	useEffect(() => {
		if (initialized) {
			getDataFromAPI();
		}
	}, [
		assetId,
		initialized,
		filterStartDate,
		filterEndDate,
		selectedDelayArea,
		filterShift,
		filterCrew,
		includeMicrodelays,
	]);

	const getFilteredDelayAreas = () => {
		if (asset === "eaf") {
			const filtered = delayAreas.filter(
				(area) => area.AssetName && area.AssetName.includes("EAF "),
			);
			if (assetId) return [{ AssetId: assetId, AssetName: "All" }, ...filtered];
		}
		return delayAreas;
	};

	if (state.isLoadingPage) {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100vh",
				}}
			>
				<ErrorModal error={error} onHide={() => setError("")} />
				<LazyLoading />
			</div>
		);
	}

	return (
		<>
			<Paper elevation={1} className={classes.root}>
				<LazyBackDropLoading open={state.isLoadingBackground} />
				<Grid container justifyContent="center" alignItems="center">
					<Grid item xs={12}>
						<Grid container justifyContent="space-between" alignItems="center">
							<Typography
								variant="h5"
								className={classes.header}
								style={{ fontWeight: 600 }}
							>
								{assetName} Delays Report
							</Typography>
						</Grid>

						<Grid item container md={12} className={classes.filterContainer}>
							<Grid item container xs={12} md={8}>
								<Paper
									style={{
										display: "flex",
										alignItems: "center",
										height: "80px",
									}}
								>
									<Filters
										onFilterChange={handleFilterChange}
										delayAreas={
											asset === "eaf" ? getFilteredDelayAreas() : null
										}
										selectedDelayArea={selectedDelayArea}
										onDelayAreaChange={handleDelayAreaChange}
										initialStartDate={filterStartDate}
										initialEndDate={filterEndDate}
										onFilterCrewChange={setFilterCrew}
										filterCrew={filterCrew as string}
										includeMicrodelays={includeMicrodelays}
										onIncludeMicrodelaysChange={handleIncludeMicrodelaysChange}
										filterShift={filterShift}
										onFilterShiftChange={setFilterShift}
									/>
								</Paper>
							</Grid>

							<Grid item xs={"auto"} style={{ marginLeft: "auto" }}>
								<ExcelButton
									onClick={handleExportToExcel}
									disabled={!state.delays || state.delays.length === 0}
								/>
							</Grid>
						</Grid>
						<NotificationSnackBar
							message={state.notificationMessage}
							onHide={() => actions.setNotificationMessage("")}
						/>
						<Grid item xs={12}>
							<DelaysReportTable
								loadingGrid={state.isLoadingGrid}
								rows={state.delays || []}
								refreshData={getDataFromAPI}
								gridRef={gridRef}
							/>
						</Grid>
					</Grid>
				</Grid>
			</Paper>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
